import { z } from 'zod';
import type { AttributeValueToken } from '@sleed/bizcommerce-api';

/**
 * Attribute-value edit form (the per-attribute "values" managed for ListOfValues attributes).
 * Field names mirror `AttributeValueToken`. Mirrors the legacy admin grid
 * (Core/client/admin/src/pages/Catalog/AttributeValues/index.js): tval required, si required.
 *
 * `tval` (Value) is the display value; the server's SharedEnrichmentService.ConvertAttributeValueForDb
 * presave coerces it into the correct typed column (ValueText/Int/Decimal/Date) per the parent
 * attribute's ValueDataType, and ConvertValueForJson reverses it on read — so a single `tval`
 * round-trips for every data type.
 *
 * The value image (img1) is intentionally not handled here yet (two-phase upload) — flagged as a follow-up.
 */
export const attributeValueSchema = z.object({
	id: z.number().optional(),
	aid: z.number(),
	cd: z.string().trim().max(50, 'validation.tooLong').optional(),
	tval: z.string().trim().min(1, 'validation.required'),
	chex: z.string().trim().max(10, 'validation.tooLong').optional(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	erpcd: z.string().trim().max(50, 'validation.tooLong').optional(),
	// Display-only existing image URL (size "S") to seed the uploader preview; upload/delete handled out-of-band.
	img1: z.string().optional()
});

export type AttributeValueFormValues = z.infer<typeof attributeValueSchema>;

export function emptyAttributeValue(attributeId: number): AttributeValueFormValues {
	return { aid: attributeId, cd: '', tval: '', chex: '', iv: true, si: 0, erpcd: '', img1: '' };
}

export function tokenToForm(token: AttributeValueToken): AttributeValueFormValues {
	return {
		id: token.id,
		aid: token.aid,
		cd: token.cd ?? '',
		tval: token.tval ?? '',
		chex: token.chex ?? '',
		iv: token.iv ?? false,
		si: token.si ?? 0,
		erpcd: token.erpcd ?? '',
		img1: token.img1 ?? ''
	};
}

export function formToToken(form: AttributeValueFormValues): AttributeValueToken {
	return {
		id: form.id,
		aid: form.aid,
		cd: form.cd?.trim() || null,
		tval: form.tval.trim(),
		chex: form.chex?.trim() || null,
		iv: form.iv,
		si: form.si,
		erpcd: form.erpcd?.trim() || null
	};
}
